/**
 * useWebSocket Hook
 *
 * Provides access to WebSocket state and methods.
 * Connection is managed globally by middleware.
 * This hook allows components to subscribe to messages and send data.
 */

import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import websocketService, { MSG_TYPE } from '../api/services/websocketService';

// Connection states (mapped from Redux)
export const WS_STATE = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
};

export { MSG_TYPE };

const useWebSocket = ({ onMessage } = {}) => {
  // Access state from Redux
  const { status: connectionState, isConnected } = useSelector(
    (state) => state.websocket
  );

  // Handle message listeners
  useEffect(() => {
    if (onMessage) {
      websocketService.addMessageListener(onMessage);
    }

    return () => {
      if (onMessage) {
        websocketService.removeMessageListener(onMessage);
      }
    };
  }, [onMessage]);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  const sendMessage = useCallback((type, payload) => {
    websocketService.sendMessage(type, payload);
  }, []);

  const sendChatMessage = useCallback((content, options) => {
    websocketService.sendChatMessage(content, options);
  }, []);

  const sendTyping = useCallback((conversationId, isTyping) => {
    websocketService.sendTyping(conversationId, isTyping);
  }, []);

  const sendReadReceipt = useCallback((conversationId, messageId) => {
    websocketService.sendReadReceipt(conversationId, messageId);
  }, []);

  return {
    connectionState,
    isConnected,
    disconnect,
    sendMessage,
    sendChatMessage,
    sendTyping,
    sendReadReceipt,
  };
};

export default useWebSocket;
